import { getDomainName, getRegionNameFromURL, setActiveRegion } from 'api-client/helpers'
import { opencloudDashboardUrl, loginWithParams, loginWithSsoUrl } from 'app/constants'
import { listRegions } from 'app/plugins/infrastructure/components/regions/actions'
import { regionsSelector } from 'app/plugins/infrastructure/components/regions/selectors'
import store, { RootState } from 'app/store'
import GlobalDropdownWithSearch, { Item } from 'core/elements/dropdown/GlobalDropdownWithSearch'
import useListAction from 'core/hooks/useListAction'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isEmpty, prop } from 'ramda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { projectAs } from 'utils/fp'
import { groupByRegion } from 'api-client/Keystone'
import ApiClient from 'api-client/ApiClient'
import { getAppBasePath, getRegionTerm, isOpenCloud } from 'core/utils/helpers'

const isDevelopment = process.env.NODE_ENV === 'development'

export const redirectToRegionLogin = (
  regionURL,
  session,
  regionId = '',
  redirectPath = undefined,
) => {
  const { tokenData, username, unscopedToken, isSsoToken } = session
  const regionEndpoint = new URL(regionURL).origin
  const appBasePath = getAppBasePath()

  // If the region is the same as the current origin, no need to redirect
  if (regionEndpoint === window.location.origin && !isDevelopment) {
    if (isOpenCloud()) {
      return window.location.replace(appBasePath + opencloudDashboardUrl)
    }
    return
  }

  // Create a redirect URL with the region endpoint and the current token data
  let redirectUrl = `${regionEndpoint}${appBasePath}${
    isSsoToken ? loginWithSsoUrl : loginWithParams
  }?issuedAt=${tokenData?.issuedAt}&username=${username}&unscopedToken=${unscopedToken}&expiresAt=${
    tokenData?.expiresAt
  }&ssoLogin=${isSsoToken}&domain=${getDomainName()}&currentRegion=${regionId}`

  // Add a redirect path if the region is opencloud. This is because by default, in base-views.tsx,
  // we redirect to the home page for opencloud, which is an all regions view, while PCD directs
  // to the region dashboard. When the user logs into a region, we want to redirect to the dashboard
  // instead of the home page.
  if (isOpenCloud() && redirectPath) {
    redirectUrl += `&redirectPath=${redirectPath}`
  }

  const url = new URL(redirectUrl)
  window.location.replace(url)
}

const GlobalRegionDropdown = ({ hideDropdown = false }) => {
  const { serviceCatalog } = ApiClient.getInstance()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { currentTenant, currentRegion } = prefs

  // Load regions
  const { loading: loadingRegions, reload: reloadRegions } = useListAction(listRegions, {
    initialLoadingState: true,
  })
  const regionsData = useSelector(regionsSelector)
  const regions = useMemo(() => {
    if (regionsData?.length === 1) {
      return regionsData
    }
    return regionsData.filter((region) => region.id !== 'Infra')
  }, [regionsData])
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { activeRegion } = session

  // When only Infra region is available
  const isSingleRegionDeployment = useMemo(() => regionsData?.length === 1, [regionsData])

  // Update isSingleRegionDeployment in session
  useEffect(() => {
    if (isSingleRegionDeployment) {
      store.dispatch(
        sessionActions.updateSession({
          isSingleRegionDeployment: true,
        }),
      )
    }
  }, [isSingleRegionDeployment])

  // const currentRegionId = useMemo(() => {
  //   if (currentRegion && find(propEq('id', currentRegion), regions)) {
  //     return currentRegion
  //   }
  //   return pipe(head, prop('id'))(regions)
  // }, [regions, currentRegion])

  // Region Options
  const options = useMemo(() => projectAs({ label: 'id', value: 'id' }, regions), [regions])

  useEffect(() => {
    // Reload region when changing the current tenant
    if (isEmpty(regions) && !loadingRegions) {
      reloadRegions(true)
    }
  }, [currentTenant, regions])

  const handleRegionSelect = useCallback(
    async (region: Item) => {
      const regionId = region?.value
      if ((regionId === currentRegion && regionId === activeRegion) || !regionId) {
        return
      }
      updateRegion(regionId)
    },
    [currentRegion, activeRegion],
  )

  // Get region endpoints
  const regionEndpoints = useMemo(() => {
    const servicesByRegion = groupByRegion(serviceCatalog)
    const regionKeys = Object.keys(servicesByRegion)
    return regionKeys.reduce((accum, region) => {
      const resmgrEndpoint = servicesByRegion?.[region]?.resmgr?.public?.url
      const baseUrl = resmgrEndpoint?.split('/resmgr')?.[0]
      return {
        ...accum,
        [region]: baseUrl,
      }
    }, {})
  }, [serviceCatalog])

  // Calculate the current region based on the service catalog and the current origin
  const calcCurrentRegion = useMemo(() => getRegionNameFromURL(), [
    window.location.host,
    serviceCatalog,
  ])

  useEffect(() => {
    // Handle for localhost
    if (
      isDevelopment ||
      window.location.host.includes('localhost') ||
      isSingleRegionDeployment ||
      isOpenCloud()
    )
      return

    // If the region is already set, no need to redirect
    if (
      calcCurrentRegion !== 'Infra' &&
      calcCurrentRegion === currentRegion &&
      calcCurrentRegion === activeRegion
    ) {
      return
    }

    const setupRegion = async () => {
      // Get the first region from the list and update as the active region
      if (calcCurrentRegion === 'Infra') {
        const firstRegion = options[0]?.value
        if (firstRegion) {
          updateRegion(firstRegion)
        }
      } else {
        // In this case no redirect is needed, just update the active region
        const newRegion = calcCurrentRegion ? calcCurrentRegion : currentRegion
        updatePrefs({ currentRegion: newRegion })
        await setActiveRegion(newRegion)
      }
    }

    setupRegion()
  }, [activeRegion, options, currentRegion, calcCurrentRegion, isSingleRegionDeployment])

  const updateRegion = useCallback(
    async (regionId) => {
      try {
        const redirectUrl = isDevelopment ? window.location.origin : regionEndpoints[regionId]
        redirectToRegionLogin(redirectUrl, session, regionId)
      } catch (err) {
        console.error(err)
      }
    },
    [isDevelopment, regionEndpoints, session],
  )

  // Hide the dropdown if only redirection logic is needed
  if (hideDropdown) {
    return null
  }

  return (
    <GlobalDropdownWithSearch
      label={getRegionTerm(true)}
      items={options}
      value={String(activeRegion)}
      onChange={handleRegionSelect}
      loading={loadingRegions}
    />
  )
}

export default GlobalRegionDropdown
